/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import { LinkOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Outlet,
  Params,
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { ScrollToTop } from '@app/components';
import { paths } from '@app/constants';
import {
  teamMembersCountQuery,
  trialDetailQuery,
  trialPermissionsQuery,
  usePermissions,
  useTokenUserData,
  useTrial,
} from '@data/hooks';
import { Namespace } from '@data/index';
import { Role, TeamMemberListView, TrialDetailsView, TrialPermissionsView } from '@data/sdk/match';
import { useAppUserStore, useOnboardingsStore } from '@data/storage';
import { getUserTeamPermissions } from '@data/utils/helpers';
import { AntdSkeleton, AntdSpace, AntdTabs, AntdTypography, BackButton, Button } from '@ui';
import { createStatusTag } from '../TrialsListPage/TrialsList/helpers';
import { useHandleTrialIsFavorite } from '../TrialsListPage/TrialsList/hooks';
import { useTrialOnboarding, useTrialRedirect } from './hooks';

const { Text } = AntdTypography;

type LoaderData = {
  permissionsData: TrialPermissionsView | undefined;
  teamMembersCountData: TeamMemberListView | undefined;
  trialData: TrialDetailsView | undefined;
};

export const trialPermissionsLoader = async (qC: QueryClient, trialId: string) => {
  const { user } = useAppUserStore.getState();
  const permissionsQuery = trialPermissionsQuery({ trialId: trialId ?? '' });
  const permissions =
    user?.currentRole === Role.TrialManager ||
    user?.currentRole === Role.PatientOrg ||
    user?.currentRole === Role.Sponsor
      ? await qC.fetchQuery<TrialPermissionsView>(permissionsQuery)
      : undefined;
  const cachedPermissions = qC.getQueryData<TrialPermissionsView>(permissionsQuery.queryKey);
  return cachedPermissions ?? permissions;
};

export const trialDetailLoader = async (
  qC: QueryClient,
  { params }: { params: Params },
): Promise<TrialDetailsView | undefined> => {
  const trialQuery = trialDetailQuery(params.trialId as string);
  const trial = await qC.fetchQuery<TrialDetailsView>(trialQuery);
  const cachedTrial = qC.getQueryData<TrialDetailsView>(trialQuery.queryKey);
  return trial ?? cachedTrial;
};

export const teamMembersCountLoader = async (qC: QueryClient, teamId: string) => {
  const query = teamMembersCountQuery({ teamId });
  const teamMembersCount = await qC.fetchQuery<TeamMemberListView>(query);
  const cachedTeamMembersCount = qC.getQueryData<TeamMemberListView>(query.queryKey);
  return teamMembersCount ?? cachedTeamMembersCount;
};

export const trialPageLoader =
  (qC: QueryClient) =>
  async ({ params }: { params: Params }): Promise<LoaderData> => {
    const teamId = useAppUserStore.getState().user?.teamId;
    let teamMembersCountData: TeamMemberListView | undefined;

    const trial = await trialDetailLoader(qC, { params });
    const permissions = await trialPermissionsLoader(qC, trial?.trialId ?? '');

    if (teamId) {
      teamMembersCountData = await teamMembersCountLoader(qC, teamId);
    }

    return {
      trialData: trial,
      permissionsData: permissions,
      teamMembersCountData,
    };
  };

const TrialPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation([Namespace.ADMIN]);
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const { trialId = '' } = useParams<{ trialId: string }>();
  const { userId } = useTokenUserData();
  const { onboardings = {} } = useOnboardingsStore();

  const { SEE_FUNNEL, IS_ADMIN, IS_TRIAL_MANAGER } = usePermissions();
  const { IS_PATIENT_ORG } = getUserTeamPermissions();

  const { data: trial, isLoading: trialIsLoading } = useTrial(trialId);
  const loaderData = useLoaderData() as LoaderData;

  useTrialOnboarding();
  useTrialRedirect(trial, loaderData?.teamMembersCountData?.items?.length === 1);

  const { handleIsFavoriteTrial } = useHandleTrialIsFavorite();

  const tabs = useMemo(
    () => [
      {
        label: t('trial_page.eligibility_criteria'),
        value: paths.criterias,
        key: `/${paths.app}/${paths.trial_recruitment}/${paths.trials}/${trialId}/${paths.criterias}`,
      },
      ...(SEE_FUNNEL && !IS_PATIENT_ORG
        ? [
            {
              label: t('trial_page.matching_patients'),
              value: paths.volunteers,
              key: `/${paths.app}/${paths.trial_recruitment}/${paths.trials}/${trialId}/${paths.volunteers}`,
              destroyInactiveTabPane: true,
            },
          ]
        : []),

      ...(IS_TRIAL_MANAGER
        ? [
            {
              label: t('trial_page.notifications'),
              value: paths.notifications,
              key: `/${paths.app}/${paths.trial_recruitment}/${paths.trials}/${trialId}/${paths.notifications}`,
            },
          ]
        : []),
      ...(IS_ADMIN
        ? [
            {
              label: t('trial_page.teams.title'),
              value: paths.teams,
              key: `/${paths.app}/${paths.trial_recruitment}/${paths.trials}/${trialId}/${paths.teams}`,
            },
          ]
        : []),
    ],
    [IS_TRIAL_MANAGER, SEE_FUNNEL, language /* trialAccessRequestsCount */],
  );

  const isOnboardingInProgress =
    !!onboardings[userId] && !onboardings[userId].trials[trialId]?.completed;

  if (!trial) return null;

  return (
    <>
      <ScrollToTop />
      <div className="flex flex-col items-start pb-6 h-full">
        <BackButton
          to={{
            pathname: `/${paths.app}/${paths.trial_recruitment}/${paths.trials}`,
            search: state?.queryParams ?? '',
          }}
        />
        <AntdSpace direction="vertical" size="large" className="w-full my-4">
          {trialIsLoading ? (
            <AntdSkeleton.Input active className="w-2/3" />
          ) : (
            <AntdSpace direction="vertical" size={12} className="w-full">
              <Text className="text-lg md:text-2xl">
                <span className="font-bold">{trial?.trialId}:</span> {trial?.officialTitle}{' '}
              </Text>
              {!isOnboardingInProgress && (
                <div className="w-full flex items-center justify-between">
                  <div className="flex gap-4 items-center">
                    <div>{createStatusTag(trial?.matchingStatus)}</div>
                    {!!trial?.processedBy?.length && (
                      <div className="flex items-center gap-1">
                        <div className="text-[10px] border-solid border-[1px] rounded-full w-fit px-[2px] py-[1px] ml-auto text-primary-600 h-fit">
                          AI
                        </div>
                        <Text>{trial?.processedBy[0].provider}</Text>
                      </div>
                    )}

                    {trial?.url && IS_ADMIN && (
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        href={trial?.url}
                        className="[&>*]:hover:text-secondary-600"
                      >
                        <LinkOutlined className="text-sm text-primary-600" />{' '}
                        <Text className="text-primary-600">{t('trial_page.open_source_link')}</Text>
                      </a>
                    )}
                    <Button
                      icon={trial?.isFavorite ? <StarFilled /> : <StarOutlined />}
                      type="text"
                      className="text-primary-600 bg-transparent px-0 disabled:text-base-500 hover:text-secondary-600 text-sm md:text-base"
                      onClick={() =>
                        handleIsFavoriteTrial({ trialId, isFavorite: trial?.isFavorite })
                      }
                      disabled={trialIsLoading}
                    >
                      {trial?.isFavorite
                        ? t('trial_page.in_favorite_list')
                        : t('trial_page.add_to_favorite_list')}
                    </Button>
                  </div>
                </div>
              )}
            </AntdSpace>
          )}
        </AntdSpace>
        {!isOnboardingInProgress && (
          <AntdTabs
            items={tabs}
            onChange={(key: string) => navigate(key, { state })}
            className="w-full [&>.ant-tabs-nav]:mb-0"
            activeKey={pathname}
            destroyInactiveTabPane={true}
          />
        )}
        <Outlet />
      </div>
    </>
  );
};

export default TrialPage;
